import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Search, Download, Activity as ActIcon, Rocket, Menu, KeyRound, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ProductMark } from "@/components/Brand";
import { ThemeToggle } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, download } from "@/lib/api";
import { CodeViewer } from "@/components/dev/CodeViewer";
import { DevAI } from "@/components/dev/DevAI";
import { SectionView, Activity } from "@/components/dev/SectionView";

const IDLE_MS = 30 * 60 * 1000;

function useIdleLogout() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const last = useRef(Date.now());
  const beat = useRef(Date.now());
  const [left, setLeft] = useState(IDLE_MS);
  useEffect(() => {
    const bump = () => { last.current = Date.now(); };
    const evs = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(async () => {
      const idle = Date.now() - last.current;
      const rem = IDLE_MS - idle;
      setLeft(rem);
      if (rem <= 0) { clearInterval(t); await logout(); toast.info("Signed out after 30 minutes of inactivity"); nav("/qlog"); return; }
      if (idle < 5 * 60 * 1000 && Date.now() - beat.current > 4 * 60 * 1000) {
        beat.current = Date.now();
        api.get("/dev/session").catch(async (e) => { if (e.response?.status === 401) { clearInterval(t); await logout(); toast.info("Your developer session expired. Please sign in again"); nav("/qlog"); } });
      }
    }, 5000);
    return () => { clearInterval(t); evs.forEach((e) => window.removeEventListener(e, bump)); };
  }, [logout, nav]);
  return Math.max(0, Math.ceil(left / 60000));
}

const Sidebar = ({ sections, cur, onSection, onView }) => {
  const areas = [...new Set(sections.map((s) => s.area))];
  return (
    <nav className="flex flex-col gap-4 px-3 pb-6" data-testid="dev-sidebar">
      <div className="flex flex-col gap-0.5">
        <button onClick={() => onView("activity")} className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] hover:bg-slate-50" data-testid="dev-nav-activity"><ActIcon className="h-4 w-4" />Live activity</button>
        <button onClick={() => onView("deploy")} className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] hover:bg-slate-50" data-testid="dev-nav-deploy"><Rocket className="h-4 w-4" />Deploy & download</button>
      </div>
      {areas.map((a) => (
        <div key={a}><div className="px-3 mb-1 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#5A6B87]/70">{a}</div>
          {sections.filter((s) => s.area === a).map((s) => (
            <button key={s.id} onClick={() => onSection(s.id)} data-testid={`dev-nav-${s.id}`}
              className={`w-full flex justify-between gap-2 text-left px-3 py-1.5 rounded-md text-[13px] border-l-[3px] ${cur === s.id ? "bg-[#F3EFFA] text-[#48217A] font-semibold border-[#48217A]" : "border-transparent text-[#3B4A63] hover:bg-slate-50"}`}>
              <span>{s.label}</span><span className="text-[11px] text-[#5A6B87]">{s.files.length}</span></button>))}
        </div>))}
    </nav>
  );
};

const Deploy = ({ map }) => (
  <div className="max-w-3xl space-y-4" data-testid="dev-deploy">
    <h2 className="font-display text-2xl font-bold">Deploy & download</h2>
    <div className="qt-card p-5"><h3 className="font-semibold mb-1">Download the code</h3><p className="text-sm text-[#5A6B87] mb-3">{map.total_files} source files. Environment files and secrets are never included.</p>
      <Button onClick={() => download("/dev/download", "isocred_full_code.zip")} className="bg-[#48217A]" data-testid="dev-download-all-2"><Download className="h-4 w-4 mr-1" />Download full codebase (.zip)</Button></div>
    <div className="qt-card p-5 text-sm space-y-2"><h3 className="font-semibold">Ship a change</h3>
      <ol className="list-decimal pl-5 space-y-1 text-[#3B4A63]"><li>Find the section and file here, or ask the AI where something lives.</li><li>Use "Suggest a change" or "Link / unlink sections" to get exact code edits.</li>
        <li>Apply the edits in the Emergent editor (or ask the Emergent agent to apply the saved .md answer).</li><li>Test in preview, then press Publish in Emergent to deploy the live app.</li></ol>
      <p className="text-xs text-[#5A6B87]">The live app cannot change or redeploy its own code; publishing always happens from Emergent.</p></div>
  </div>
);

function SearchResults({ results, onOpen }) {
  return (
    <div data-testid="dev-search-results"><h2 className="font-display text-xl font-bold mb-3">{results.length} matches</h2>
      <div className="qt-card divide-y">{results.map((r, i) => <button key={i} onClick={() => onOpen(r.path, r.line)} className="block w-full text-left px-3 py-2 hover:bg-[#F3EFFA]/50">
        <div className="font-mono text-[11px] text-[#48217A]">{r.path}{r.line ? `:${r.line}` : ""}</div><div className="font-mono text-xs truncate">{r.text}</div></button>)}</div></div>
  );
}

export default function DevPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const mins = useIdleLogout();
  const [map, setMap] = useState(null);
  const [view, setView] = useState({ kind: "section", id: "auth" });
  const [file, setFile] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [act, setAct] = useState(null);
  const [ctx, setCtx] = useState({ mode: "locate" });
  const [menu, setMenu] = useState(false);
  const loadMap = useCallback(() => api.get("/dev/map").then((r) => setMap(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { loadMap(); }, [loadMap]);
  useEffect(() => {
    if (view.kind !== "activity") { return undefined; }
    const f = () => api.get("/dev/recent", { headers: { "X-Idle-Poll": "1" } }).then((r) => setAct(r.data)).catch(() => {});
    f(); const t = setInterval(f, 30000); return () => clearInterval(t);
  }, [view.kind]);
  if (!map) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#48217A]" /></div>;
  const byId = Object.fromEntries(map.sections.map((s) => [s.id, s]));
  const openFile = async (path, line) => {
    try { const { data } = await api.get("/dev/file", { params: { path } }); setFile(data); setView({ kind: "file", line }); setMenu(false); } catch (e) { toast.error(errMsg(e)); }
  };
  const openSection = (id) => { setView({ kind: "section", id }); setMenu(false); };
  const doSearch = async (e) => { e.preventDefault(); try { setResults((await api.get("/dev/search", { params: { q } })).data); setView({ kind: "search" }); } catch (x) { toast.error(errMsg(x)); } };
  const sec = byId[view.id] || map.sections[0];
  const side = <Sidebar sections={map.sections} cur={view.kind === "section" ? sec.id : file?.sections?.[0]} onSection={openSection} onView={(k) => { setView({ kind: k }); setMenu(false); }} />;
  return (
    <div className="min-h-screen bg-[#F4F6F8] flex" data-testid="dev-portal">
      <aside className="hidden lg:flex flex-col w-[260px] shrink-0 bg-white border-r h-screen sticky top-0 overflow-y-auto"><div className="px-5 py-5"><ProductMark /></div>{side}</aside>
      <Sheet open={menu} onOpenChange={setMenu}><SheetContent side="left" className="w-[280px] p-0 overflow-y-auto"><div className="px-5 py-5"><ProductMark /></div>{side}</SheetContent></Sheet>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b flex flex-wrap items-center px-4 lg:px-6 py-2 gap-2">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setMenu(true)} data-testid="dev-mobile-menu"><Menu className="h-5 w-5" /></button>
          <span className="text-xs font-semibold bg-[#172238] text-white rounded px-2 py-0.5">DEVELOPER</span>
          <form onSubmit={doSearch} className="flex-1 min-w-[180px] max-w-md relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-[#5A6B87]" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, files, text…" className="pl-8 h-9" data-testid="dev-search-input" /></form>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-[#5A6B87]" data-testid="dev-idle-timer"><Timer className="h-3.5 w-3.5" />{mins} min</span>
            <Button size="sm" variant="outline" onClick={() => download("/dev/download", "isocred_full_code.zip")} data-testid="dev-download-all"><Download className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Full code</span></Button>
            <ThemeToggle testid="dev-theme-toggle" />
            <Button size="sm" variant="ghost" onClick={() => nav("/change-password")} data-testid="dev-change-password"><KeyRound className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={async () => { await logout(); nav("/qlog"); }} data-testid="dev-logout"><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        {!map.frontend_source && <div className="mx-4 lg:mx-6 mt-3 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-[#8A5A00]">Frontend source files are not present on this server (built app only). Backend code is fully available.</div>}
        <main className="flex-1 p-4 lg:p-6 grid 2xl:grid-cols-[1fr_400px] xl:grid-cols-[1fr_360px] gap-5 min-w-0">
          <div className="min-w-0">
            <div className="text-xs text-[#5A6B87] mb-3" data-testid="dev-user">{user?.email} · {map.total_files} files in {map.sections.length} sections</div>
            {view.kind === "section" && <SectionView sec={sec} byId={byId} onOpen={openFile} onSection={openSection} onAsk={(id) => setCtx({ ...ctx, mode: "explain", section_id: id, path: null })} onLink={(id) => setCtx({ mode: "link", section_id: id, target: "", path: null })} />}
            {view.kind === "file" && file && <CodeViewer file={file} line={view.line} onOpen={openFile} sectionLabel={file.sections.map((s) => byId[s]?.label || s).join(", ")} onAsk={(p) => setCtx({ ...ctx, mode: "explain", path: p, section_id: file.sections[0] !== "other" ? file.sections[0] : null })} />}
            {view.kind === "search" && <SearchResults results={results} onOpen={openFile} />}
            {view.kind === "activity" && (act ? <Activity data={act} onOpen={openFile} /> : <Loader2 className="h-5 w-5 animate-spin" />)}
            {view.kind === "deploy" && <Deploy map={map} />}
          </div>
          <div className="min-w-0"><div className="xl:sticky xl:top-20"><DevAI sections={map.sections} ctx={ctx} setCtx={setCtx} /></div></div>
        </main>
      </div>
    </div>
  );
}
